import React, { PropsWithChildren } from "react";
import { Box, Skeleton, Stack, BoxProps, Divider } from "@mui/material";
import dynamic from "next/dynamic";
import { useTranslation } from "next-i18next";
import { MAP_CHART_WIDTH } from "../../components/map-chart/constants";
import { LEGEND_CONTAINER_WIDTH } from "../../components/pie-chart/constants";
import { CHART_VARIANTS, ChartVariant } from "../chart.type";
import { CHART_GENERAL_HEIGHT } from "../constants";

const IconWorldMap = dynamic(() => import("./icon-map-world").then(module => module.IconWorldMap), {
    ssr: false,
});

// Constants
const LEGEND_ITEM_COUNT = 6;
const PIE_CHART_SIZE = 240;
const BORDER_WIDTH = 23;

// Test IDs
const CHART_SKELETON_TEST_ID = "chart-skeleton";

interface IChartLoadingSkeletonProps extends BoxProps {
    disableAnimation?: boolean;
    variant?: ChartVariant;
}

export const ChartLoadingSkeleton = ({
    disableAnimation = false,
    variant = CHART_VARIANTS.PIE,
    children,
    ...boxProps
}: PropsWithChildren<IChartLoadingSkeletonProps>) => {
    const { t } = useTranslation("wf-detail");

    return (
        <Box
            display="flex"
            flexDirection={["column", "row"]}
            pt={4}
            gap={5}
            width="100%"
            role="alert"
            aria-busy="true"
            alignItems="center"
            aria-label={t("charts.aria-label-loading")}
            data-testid={CHART_SKELETON_TEST_ID}
            {...boxProps}
        >
            {/* DEV Note: this is done intentionally to preload the icon, to avoid loading in main bundle, but defer its loading */}
            <IconWorldMap sx={{ display: "none" }}></IconWorldMap>
            {variant === CHART_VARIANTS.MAP && <IconWorldMap sx={{ width: MAP_CHART_WIDTH, height: CHART_GENERAL_HEIGHT }} />}

            {variant === CHART_VARIANTS.PIE && (
                <Box sx={{ display: "flex", justifyContent: "center", px: 2, alignItems: "center", height: ["auto", CHART_GENERAL_HEIGHT] }}>
                    <Skeleton
                        variant="circular"
                        width={PIE_CHART_SIZE}
                        height={PIE_CHART_SIZE}
                        animation={disableAnimation ? false : "pulse"}
                        sx={{
                            mt: [0, "-35px"],
                            backgroundColor: "transparent",
                            border: `${BORDER_WIDTH}px solid`,
                            borderColor: disableAnimation ? "grey.50" : "grey.300",
                        }}
                    />
                </Box>
            )}

            {/* Legend items below the chart */}
            <Stack width="100%" maxWidth={LEGEND_CONTAINER_WIDTH} ml="auto" mb="auto">
                {Array.from({ length: LEGEND_ITEM_COUNT }).map((_, index) => (
                    <React.Fragment key={index}>
                        <Box
                            pt={1.5}
                            pb={1.5}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <Skeleton
                                variant="circular"
                                animation={disableAnimation ? false : "pulse"}
                                sx={{ width: 10, height: 10, mr: 1, backgroundColor: disableAnimation ? "grey.50" : undefined }}
                            />
                            <Skeleton
                                variant="rectangular"
                                animation={disableAnimation ? false : "pulse"}
                                sx={{ borderRadius: 0.5, width: 144, height: 16, backgroundColor: disableAnimation ? "grey.50" : undefined }}
                            />
                            <Skeleton
                                variant="rectangular"
                                animation={disableAnimation ? false : "pulse"}
                                sx={{
                                    ml: "auto",
                                    borderRadius: 0.5,
                                    width: 48,
                                    height: 16,
                                    backgroundColor: disableAnimation ? "grey.50" : undefined,
                                }}
                            />
                        </Box>
                        <Divider sx={{ borderColor: disableAnimation ? "grey.50" : undefined }} />
                    </React.Fragment>
                ))}
            </Stack>
            {children}
        </Box>
    );
};
